import {
  Component,
  inject,
  OnDestroy,
  Injectable,
  OnInit,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
} from '@angular/forms';
import { Subject, Observable, forkJoin } from 'rxjs';
import { takeUntil, startWith, map } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
  NativeDateAdapter,
  DateAdapter,
} from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { RolesService } from '../../../shared/services/roles/roles.service';
import { CommonModule, registerLocaleData } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { RestaurantService } from '../../../shared/services/restaurant/restaurant.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { QeqService } from 'src/app/shared/services/qeq/qeq.service';
import { QeqUser } from 'src/app/shared/interfaces/qeq.interface';
import { BlockedDate } from 'src/app/shared/interfaces/restaurant.interface';
import localeEs from '@angular/common/locales/es';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

registerLocaleData(localeEs);

// Custom DateAdapter to set Monday as first day of week
@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1; // Monday (0 = Sunday, 1 = Monday, etc.)
  }
}

// Define custom date formats
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

interface Permission {
  read: boolean;
  write: boolean;
  delete: boolean;
}

interface PolicyData {
  name: string;
  description: string;
  politics: {
    functionality: string;
    permissions: Permission;
  }[];
}

// Update Employee interface to match QeqUser for consistency
interface Employee extends QeqUser {
  // No need to redefine properties already in QeqUser
}

@Component({
  selector: 'app-reservation-form',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    FormsModule,
    MatTimepickerModule,
    RouterModule,
    CommonModule,
    LoaderComponent,
    QuillModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './reservation-form.component.html',
  styleUrls: [
    './reservation-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class ReservationFormComponent implements OnInit, OnDestroy {
  private _snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();
  modules: any;

  // Add return path property
  returnPath: string = '/home/dining/general';

  // Edit mode properties
  isEditMode = false;
  reservationId: number | null = null;
  reservationDate: string | null = null; // Store the date when editing

  // Cook screen mode - only allows reservations for today
  isCookScreenMode = false;

  employees: Employee[] = [];
  filteredEmployees!: Observable<Employee[]>;
  reservationForm: FormGroup;
  selectedEmployee: Employee | null = null;
  myroles: any = {};
  currentEmployee: Employee | null = null;

  // Blocked dates for validation
  private blockedDates: BlockedDate[] = [];

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  constructor(
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private restaurantService: RestaurantService,
    private router: Router,
    private quillConfigService: QuillConfigService,
    private route: ActivatedRoute,
    private rolesService: RolesService,
    private storageService: StorageService,
    private qeqService: QeqService // Add QeqService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();

    this.reservationForm = this.fb.group({
      employeeName: ['', Validators.required],
      guestCount: [
        '',
        [Validators.required, Validators.min(0), Validators.max(10)],
      ],
      isCeliac: [false, Validators.required],
      celiacObservation: [''],
      selectedDate: [
        null, // Will be set after determining the mode
        [Validators.required, this.dateValidator.bind(this)],
      ],
      guestsObservation: [''],
      guests: this.fb.array([]),
    });

    // Make celiacObservation required only when isCeliac is true
    const isCeliacControl = this.reservationForm.get('isCeliac');
    const celiacObsControl = this.reservationForm.get('celiacObservation');

    // Helper to toggle validators
    const toggleCeliacValidators = (value: boolean | null) => {
      if (!celiacObsControl) return;
      if (value) {
        celiacObsControl.setValidators([Validators.required]);
      } else {
        celiacObsControl.clearValidators();
      }
      celiacObsControl.updateValueAndValidity({ emitEvent: false });
    };

    // Subscribe to changes
    isCeliacControl?.valueChanges
      .pipe(startWith(isCeliacControl.value))
      .subscribe((val) => {
        toggleCeliacValidators(!!val);
      });

    // Update guests array when guestCount changes with validation
    this.reservationForm.get('guestCount')?.valueChanges.subscribe((count) => {
      // Validate the count before updating guest array
      if (count !== null && count !== undefined && !isNaN(count)) {
        // Enforce min and max limits
        const validCount = Math.min(Math.max(count, 0), 10);

        // Update form value if it was outside valid range
        if (validCount !== count) {
          this.reservationForm.get('guestCount')?.setValue(validCount, {
            emitEvent: false,
          });
        }

        this.updateGuestsArray(validCount);
      } else {
        this.updateGuestsArray(0);
      }
    });
  }

  // Get guests FormArray
  get guests(): FormArray {
    return this.reservationForm.get('guests') as FormArray;
  }

  // Check if any guest is celiac
  get hasCeliacGuests(): boolean {
    return this.guests.controls.some(
      (control) => control.get('isCeliac')?.value === true
    );
  }

  // Create a new guest form group with celiac checkbox and comments field
  createGuestFormGroup(): FormGroup {
    return this.fb.group({
      isCeliac: [false],
      celiacComments: [''],
    });
  }

  // Update the guests array based on the guest count
  updateGuestsArray(count: number): void {
    // Ensure count is within valid range
    count = Math.min(Math.max(0, count), 10);

    const currentLength = this.guests.length;

    if (currentLength < count) {
      // Add more guest form groups
      for (let i = currentLength; i < count; i++) {
        this.guests.push(this.createGuestFormGroup());
      }
    } else if (currentLength > count) {
      // Remove excess guest form groups
      for (let i = currentLength - 1; i >= count; i--) {
        this.guests.removeAt(i);
      }
    }
  }

  loadRoles() {
    // Get permissions using the service method
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'kitchen_screen',
      this.storageService,
      'general'
    );

    if (permissions) {
      // Store permissions in myroles
      this.myroles = permissions;

      // Check write permission and redirect if needed
      this.rolesService.checkWritePermission(
        'kitchen_screen',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/dining/general',
        'No tienes permisos para editar una reserva',
        'general'
      );
    } else {
      //  console.log('No user roles found in storage');
    }
  }

  ngOnInit() {
    this.loaderService.showLoader();

    // Check if we're in edit mode by looking for an ID in the route params
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.isEditMode = true;
        this.reservationId = +id; // Convert string id to number
      }
    });

    // Determine the correct return path and mode based on the navigation history
    this.determineReturnPath();

    // Set date based on mode
    if (this.isCookScreenMode && !this.isEditMode) {
      // In cook screen mode, always set today's date and remove validation requirement
      this.reservationForm.patchValue({
        selectedDate: new Date(),
      });
      // Remove validation requirement for selectedDate in cook screen mode
      this.reservationForm.get('selectedDate')?.clearValidators();
      this.reservationForm.get('selectedDate')?.updateValueAndValidity();
    }

    // Load blocked dates for validation (only needed in create mode)
    if (!this.isEditMode) {
      this.loadBlockedDates();
    }

    // Load employees first in all cases
    this.getEmployees();
    this.loadRoles();
  }

  // Add method to determine the return path and mode
  private determineReturnPath() {
    // Get the current URL
    const currentUrl = this.router.url;

    // Set the return path and mode based on where the component was accessed from
    if (currentUrl.includes('/dining/cook-screen')) {
      this.returnPath = '/home/dining/cook-screen';
      this.isCookScreenMode = true; // Set cook screen mode
    } else {
      this.returnPath = '/home/dining/general';
      this.isCookScreenMode = false;
    }

    // console.log('Return path set to:', this.returnPath, 'Cook screen mode:', this.isCookScreenMode);
  }

  // Method to load reservation data for edit mode
  loadReservationData(id: number) {
    //  this.loaderService.showLoader();
    //  console.log('Loading reservation data for ID:', id);

    // Use forkJoin to make parallel requests for reservation and guests data
    forkJoin({
      reservation: this.restaurantService.getReservation(id),
      guests: this.restaurantService.getReservationGuests(id),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const reservation = result.reservation;
          this.currentEmployee =
            this.employees.find(
              (emp) => emp.personnelnumber === reservation.employee_external_id
            ) || null;
          const guests = result.guests;

          // Store the reservation date for later use
          if (reservation.date) {
            this.reservationDate = reservation.date;
          }

          // Find the matching employee from the employees list using employee_external_id
          this.selectedEmployee =
            this.employees.find(
              (emp) => emp.personnelnumber === reservation.employee_external_id
            ) || null;

          if (this.selectedEmployee) {
            // In edit mode, clear validators for selectedDate since it's not shown
            this.reservationForm.get('selectedDate')?.clearValidators();
            this.reservationForm.get('selectedDate')?.updateValueAndValidity();

            // Update form with reservation data including main celiac information
            this.reservationForm.patchValue({
              employeeName: this.selectedEmployee,
              guestCount: reservation.guests_number || 0,
              guestsObservation: reservation.guests_observation || '',
              isCeliac: reservation.is_celiac || false, // Load main celiac flag
              celiacObservation: reservation.celiac_comment || '', // Load main celiac comment
            });

            // Handle guests data if available
            if (Array.isArray(guests) && guests.length > 0) {
              // First make sure the guest count matches
              this.reservationForm.get('guestCount')?.setValue(guests.length);

              // Clear existing guests array first
              while (this.guests.length) {
                this.guests.removeAt(0);
              }

              // Add guests from the API response
              guests.forEach((guest) => {
                const guestGroup = this.fb.group({
                  isCeliac: [guest.is_celiac || false],
                  celiacComments: [guest.celiac_comment || ''],
                });

                this.guests.push(guestGroup);
              });
            }
          } else {
            this.openSnackBar(
              'Error: No se pudo encontrar el empleado asociado a esta reserva'
            );
          }
        },
        error: (error) => {
          console.error('Error loading reservation data:', error);
          this.openSnackBar('Error al cargar los datos de la reserva');
          this.loaderService.hideLoader();
        },
        complete: () => {
          //   console.log('Reservation data load complete');
          this.loaderService.hideLoader();
        },
      });
  }

  // Method to load blocked dates for validation
  loadBlockedDates() {
    this.restaurantService
      .getBlockedDays()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blockedDates: BlockedDate[]) => {
          this.blockedDates = blockedDates.filter((date) => !date.is_deleted);
          // console.log('Blocked dates loaded:', this.blockedDates);
        },
        error: (error: any) => {
          console.error('Error fetching blocked dates:', error);
          // Don't show error to user as this is not critical for form functionality
        },
      });
  }

  // Updated method to use QeqService
  getEmployees() {
    // Ensure loader is visible
    this.loaderService.showLoader();

    this.qeqService
      .getQeQ()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          //  console.log('QeQ data loaded successfully');
          if (response && response.data && Array.isArray(response.data)) {
            this.employees = response.data.sort((a: Employee, b: Employee) =>
              a.name.localeCompare(b.name)
            );
            this.filteredEmployees = this.reservationForm
              .get('employeeName')!
              .valueChanges.pipe(
                startWith(''),
                map((value) => this._filterEmployees(value))
              );

            // If in edit mode, load the reservation data after employees are loaded
            if (this.isEditMode && this.reservationId) {
              this.loadReservationData(this.reservationId);
            }
          } else {
            console.error('Invalid QeQ data format:', response);
          }
        },
        error: (error: any) => {
          console.error('Error fetching QeQ data:', error);
          this.openSnackBar('Error al cargar los datos de empleados');
          this.loaderService.hideLoader();
        },
        complete: () => {
          //  console.log('QeQ data fetch complete');
          this.loaderService.hideLoader();
        },
      });
  }

  private _filterEmployees(value: any): Employee[] {
    if (!value) {
      return this.employees;
    }

    // If value is already an Employee object, return all employees (no filtering needed)
    if (typeof value === 'object') {
      return this.employees;
    }

    // Filter by both name and personnelnumber
    const filterValue = value.toLowerCase();
    return this.employees.filter(
      (employee) =>
        employee.name.toLowerCase().includes(filterValue) ||
        employee.personnelnumber.toString().includes(filterValue)
    );
  }

  displayFn(employee: Employee): string {
    return employee ? employee.name : '';
  }

  onEmployeeSelected(event: any) {
    this.selectedEmployee = event.option.value;
    // console.log('Selected employee:', this.selectedEmployee);
  }

  // ===== Date validation methods =====

  /**
   * Checks if a given date is a weekend (Saturday or Sunday)
   * @param date Date to check
   * @returns boolean True if weekend, false otherwise
   */
  private isWeekend(date: Date | string): boolean {
    const d =
      typeof date === 'string'
        ? new Date(date + 'T12:00:00') // Use noon to avoid timezone issues
        : date;

    const dayOfWeek = d.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  }

  /**
   * Checks if a date is blocked for reservations
   * @param date Date to check
   * @returns boolean True if date is blocked, false otherwise
   */
  private isBlockedDate(date: Date | string): boolean {
    const dateString =
      typeof date === 'string'
        ? date.split('T')[0] // Extract YYYY-MM-DD part
        : this.formatDateForComparison(date);

    return this.blockedDates.some(
      (blockedDate) =>
        blockedDate.date === dateString && blockedDate.block_type === 'HOLIDAY' // Only holidays prevent reservations
    );
  }

  /**
   * Formats date for comparison in YYYY-MM-DD format
   * @param date Date to format
   * @returns string Date in YYYY-MM-DD format
   */
  private formatDateForComparison(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Formats date for display in DD/MM/YYYY format
   * @param date Date to format
   * @returns string Date in DD/MM/YYYY format
   */
  formatDateForDisplay(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Gets today's date
   * @returns Date object for today
   */
  getTodaysDate(): Date {
    return new Date();
  }

  /**
   * Checks if a date is valid for making reservations
   * @param date Date to check
   * @returns boolean True if date is valid for reservations, false otherwise
   */
  private isValidReservationDate(date: Date | string): boolean {
    return !this.isWeekend(date) && !this.isBlockedDate(date);
  }

  /**
   * Date filter function for mat-datepicker to disable invalid dates
   * @param date Date to check
   * @returns boolean True if date should be enabled, false if disabled
   */
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;

    // Only allow dates from today onwards
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) return false;

    // Check basic validation (weekend/holiday)
    if (!this.isValidReservationDate(date)) return false;

    // Check if cutoff time has passed (including for today)
    if (!this.isBookingAvailable(date)) return false;

    return true;
  };

  /**
   * Checks if a reservation can still be made or modified for a given date
   * based on the 15:00 cutoff time of the last AVAILABLE working day before the date.
   * Rules:
   *  - Normal rule: cutoff = 15:00 of the previous working (non weekend) day.
   *  - If the previous day is a HOLIDAY or a weekend, go back until a normal working day.
   *  - NO_SERVICE and EVENT days are still considered working days for cutoff calculation.
   *  - For Mondays this naturally skips the weekend (Fri is used unless it's a holiday).
   *  - Weekends are treated as blocked for cutoff traversal purposes (cannot modify during them).
   * @param date The date to check for booking availability
   * @returns boolean True if booking is still available, false if cutoff has passed
   */
  private isBookingAvailable(date: Date | string): boolean {
    const targetDate =
      typeof date === 'string'
        ? new Date(date + 'T00:00:00')
        : new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            0,
            0,
            0
          );

    // Helper: format date -> YYYY-MM-DD
    const format = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`;

    // Build a quick lookup set of HOLIDAY dates only (NO_SERVICE and EVENT are working days)
    const blockedSet = new Set(
      this.blockedDates
        .filter(
          (d) => d && d.date && d.block_type && d.block_type === 'HOLIDAY'
        )
        .map((d) =>
          typeof d.date === 'string' ? d.date.split('T')[0] : d.date
        )
    );

    // Traverse backwards from the day before the target until we find a working day (not weekend, not holiday).
    const cutoffBase = new Date(targetDate);
    cutoffBase.setDate(cutoffBase.getDate() - 1);

    let safety = 0; // prevent infinite loop (should never exceed 30 iterations)
    while (safety < 30) {
      const dayStr = format(cutoffBase);
      const isWeekend = this.isWeekend(cutoffBase);
      const isHoliday = blockedSet.has(dayStr);

      if (!isWeekend && !isHoliday) {
        break; // Found valid cutoff base day (NO_SERVICE and EVENT are working days)
      }
      cutoffBase.setDate(cutoffBase.getDate() - 1); // step back further
      safety++;
    }

    // If we exceeded safety (extreme edge case), allow booking (fail-open)
    if (safety >= 30) return true;

    // Set cutoff time (15:00) on the identified base day
    cutoffBase.setHours(15, 0, 0, 0);

    const now = new Date();
    return now < cutoffBase;
  }

  /**
   * Custom validator for the date form control
   * @param control Form control to validate
   * @returns ValidationErrors or null if valid
   */
  private dateValidator(control: any): { [key: string]: any } | null {
    if (!control.value) return null;

    const date =
      control.value instanceof Date ? control.value : new Date(control.value);

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return { pastDate: true };
    }

    // Check if date is weekend
    if (this.isWeekend(date)) {
      return { weekend: true };
    }

    // Check if date is blocked holiday
    if (this.isBlockedDate(date)) {
      return { blockedDate: true };
    }

    // Check if cutoff time has passed for this date (including today)
    if (!this.isBookingAvailable(date)) {
      return { cutoffPassed: true };
    }

    return null;
  }

  onSubmit() {
    // Handle validation differently based on mode
    let isFormValidForSubmission = false;

    if (this.isEditMode) {
      // For edit mode, only validate required fields (skip date validation)
      isFormValidForSubmission = !!(
        this.reservationForm.get('employeeName')?.valid &&
        this.reservationForm.get('guestCount')?.valid &&
        this.selectedEmployee
      );
    } else if (this.isCookScreenMode) {
      // For cook screen mode, validate differently as selectedDate is automatically set to today
      isFormValidForSubmission = !!(
        this.reservationForm.get('employeeName')?.valid &&
        this.reservationForm.get('guestCount')?.valid &&
        this.selectedEmployee
      );
    } else {
      // For normal mode, validate all fields including selectedDate
      isFormValidForSubmission = !!(
        this.reservationForm.valid && this.selectedEmployee
      );
    }

    if (isFormValidForSubmission) {
      // Additional validation for non-edit mode (skip for cook screen mode as it uses today's date)
      if (!this.isEditMode && !this.isCookScreenMode) {
        const selectedDate = this.reservationForm.get('selectedDate')?.value;
        if (selectedDate) {
          if (!this.isValidReservationDate(selectedDate)) {
            this.openSnackBar(
              'La fecha seleccionada no es válida para hacer reservas'
            );
            return;
          }

          // Check cutoff time (including for today)
          if (!this.isBookingAvailable(selectedDate)) {
            this.openSnackBar(
              'El tiempo límite para hacer reservas para esta fecha ha pasado. Las reservas deben realizarse antes de las 15:00 del día laboral anterior.'
            );
            return;
          }
        }
      }

      this.loaderService.showLoader();

      const guestsObservation =
        this.reservationForm.get('guestsObservation')?.value || '';

      const guestsData = this.guests.controls.map((control, index) => {
        return {
          guestNumber: index + 1,
          isCeliac: control.get('isCeliac')?.value,
          celiacComments: control.get('celiacComments')?.value,
        };
      });

      // Create the guests_info array with the format [is_celiac, celiac_comment, guests_observation]
      const guestsInfo = guestsData.map((guest) => [
        guest.isCeliac,
        guest.celiacComments || '',
        guestsObservation || '', // Use the same observation for all guests
      ]);

      // Use the stored date if in edit mode, otherwise use selected date or today for cook screen
      let formattedDate: string;
      if (this.isEditMode && this.reservationDate) {
        formattedDate = this.reservationDate;
      } else if (this.isCookScreenMode) {
        // For cook screen mode, always use today's date
        formattedDate = this.formatDateForComparison(new Date());
      } else {
        // Get selected date in YYYY-MM-DD format for new reservations
        const selectedDate = this.reservationForm.get('selectedDate')?.value;
        if (!selectedDate) {
          this.openSnackBar('Por favor seleccione una fecha para la reserva');
          return;
        }
        // Format date properly to avoid timezone issues
        formattedDate = this.formatDateForComparison(selectedDate);
      }

      // Get external_id from localStorage.serveto_user_profile
      let externalId = '';
      const userProfileStr = localStorage.getItem('serveto_user_profile');
      if (userProfileStr) {
        try {
          const userProfile = JSON.parse(userProfileStr);
          externalId = userProfile.external_id || '';
        } catch (e) {
          externalId = '';
        }
      }

      // Get the celiac values - only use celiacObservation if isCeliac is true
      const isCeliac = this.reservationForm.get('isCeliac')?.value || false;
      const celiacComment = isCeliac
        ? this.reservationForm.get('celiacObservation')?.value || ''
        : ''; // Send blank if not celiac

      const reservationData = {
        employee: this.selectedEmployee!.personnelnumber,
        date: formattedDate,
        meal_type: 'LUNCH',
        device: 'DESKTOP',
        guests_number: this.reservationForm.get('guestCount')?.value,
        status: this.isCookScreenMode && !this.isEditMode ? 'EATEN' : 'PENDING',
        charge: true,
        is_celiac: isCeliac,
        celiac_comment: celiacComment, // Use the conditional value
        guests_observation: guestsObservation || '',
        guests_info: guestsInfo,
      };

      // Different behavior for edit mode vs. create mode
      if (this.isEditMode && this.reservationId) {
        // Update existing reservation
        //    console.log(
        //  'Edit mode - updating reservation:',
        //   this.reservationId,
        //   reservationData
        //  );
        this.restaurantService
          .putReservation(this.reservationId, reservationData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              //  console.log('Reservation updated successfully:', reservationData);
              this.openSnackBar('Reserva actualizada correctamente');
              this.loaderService.hideLoader();
              this.router.navigate([this.returnPath]);
            },
            error: (error) => {
              this.loaderService.hideLoader();
              console.error('Error updating reservation:', error);

              if (error?.error?.error) {
                this.openSnackBar('Error: ' + error.error.error);
              } else if (error?.error?.message) {
                this.openSnackBar('Error: ' + error.error.message);
              } else {
                this.openSnackBar('Error al actualizar la reserva');
              }
            },
          });
      } else {
        // Create new reservation
        this.restaurantService
          .postReservation(reservationData)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              //  console.log(
              //      'Reservation submitted successfully:',
              //    reservationData
              //    );
              this.openSnackBar('Reserva añadida correctamente');
            },
            error: (error) => {
              this.loaderService.hideLoader();
              console.error('Error submitting reservation:', error);

              // Check if error contains the specific validation message about existing reservation
              if (
                error.error &&
                (error.error.includes('Este empleado ya tiene una reserva') ||
                  (Array.isArray(error.error) &&
                    error.error.some((err: string) =>
                      err.includes('Este empleado ya tiene una reserva')
                    )))
              ) {
                this.openSnackBar(
                  'Error al añadir la reserva, el trabajador ya tiene una reserva existente hoy'
                );
              } else {
                this.openSnackBar('Error al añadir la reserva');
              }
            },
            complete: () => {
              this.loaderService.hideLoader();
              //    console.log('Reservation submission complete');

              // Navigate back to the return path
              // Force navigation even if we're already on the same route by using a navigation ID
              // This helps trigger the NavigationEnd event properly when returning
              if (this.returnPath === '/home/dining/cook-screen') {
                this.router.navigateByUrl('/home/dining/cook-screen', {
                  replaceUrl: true,
                  state: { refreshData: true },
                });
              } else {
                this.router.navigate([this.returnPath]);
              }
            },
          });
      }

      // console.log('Submitting reservation:', reservationData);
      // console.log('LocalStorage contents:', localStorage);
    } else {
      this.openSnackBar('Por favor complete todos los campos requeridos');
      this.reservationForm.markAllAsTouched();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
